import {
  connectionStatus,
  latencyJitter,
  latencyRtt,
  reconnectAttempts,
  serverUrl,
} from '../../state/connection';
import {
  debugEnabled,
  debugFlags,
  fpsSignal,
  frameTimeSignal,
  type PlacementDebugInfo,
  placementDebug,
} from '../../state/debug';
import {
  frameProfileVersion,
  getProfileSnapshot,
  type ProfileSnapshot,
  type SectionStats,
} from '../../state/frameProfiler';
import { t } from '../../state/i18n';

const parseServerInfo = ({ url }: { url: string }): { host: string; port: string } => {
  try {
    const parsed = new URL(url);
    return {
      host: parsed.hostname,
      port: parsed.port || (parsed.protocol === 'wss:' ? '443' : '80'),
    };
  } catch {
    return { host: 'unknown', port: '-' };
  }
};

const getLatencyColor = ({ rtt }: { rtt: number }): string => {
  if (rtt < 50) {
    return 'text-emerald-400';
  }
  if (rtt < 100) {
    return 'text-yellow-400';
  }
  if (rtt < 200) {
    return 'text-orange-400';
  }
  return 'text-red-400';
};

const getThresholdColor = ({
  value,
  good,
  warn,
  ascending,
}: {
  value: number;
  good: number;
  warn: number;
  ascending: boolean;
}): string => {
  if (ascending) {
    if (value >= good) {
      return 'text-emerald-400';
    }
    if (value >= warn) {
      return 'text-yellow-400';
    }
    return 'text-red-400';
  }
  if (value <= good) {
    return 'text-emerald-400';
  }
  if (value <= warn) {
    return 'text-yellow-400';
  }
  return 'text-red-400';
};

const yesNo = ({ value }: { value: boolean }): string =>
  value ? t('ui.debug.yes') : t('ui.debug.no');

const boolColor = ({ value, invert }: { value: boolean; invert?: boolean }): string =>
  (invert ? !value : value) ? 'text-emerald-400' : 'text-red-400';

const DebugRow = ({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) => (
  <div class="flex justify-between gap-4">
    <span class="text-slate-500">{label}</span>
    <span class={valueClass ?? 'text-slate-300'}>{value}</span>
  </div>
);

const DebugSection = ({
  title,
  children,
}: {
  title: string;
  children: preact.ComponentChildren;
}) => (
  <div class="space-y-1">
    <div class="text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-700/50 pb-1">
      {title}
    </div>
    {children}
  </div>
);

const PerformanceDebugSection = () => {
  const fps = fpsSignal.value;
  const frameTime = frameTimeSignal.value;
  return (
    <DebugSection title={t('ui.debug.performance')}>
      <DebugRow
        label={t('ui.debug.fps')}
        value={String(fps)}
        valueClass={getThresholdColor({ value: fps, good: 55, warn: 30, ascending: true })}
      />
      <DebugRow
        label={t('ui.debug.frame')}
        value={`${frameTime.toFixed(1)}ms`}
        valueClass={getThresholdColor({ value: frameTime, good: 18, warn: 33, ascending: false })}
      />
    </DebugSection>
  );
};

const NetworkDebugSection = () => {
  const rtt = latencyRtt.value;
  const jitter = latencyJitter.value;
  const status = connectionStatus.value;
  const url = serverUrl.value;
  const reconnects = reconnectAttempts.value;
  const serverInfo = parseServerInfo({ url });

  return (
    <DebugSection title={t('ui.debug.network')}>
      <DebugRow label={t('ui.debug.server')} value={serverInfo.host} />
      <DebugRow label={t('ui.debug.port')} value={serverInfo.port} />
      <DebugRow
        label={t('ui.debug.status')}
        value={status}
        valueClass={status === 'connected' ? 'text-emerald-400' : 'text-yellow-400'}
      />
      <DebugRow
        label={t('ui.debug.latency')}
        value={`${Math.round(rtt)}ms`}
        valueClass={getLatencyColor({ rtt })}
      />
      <DebugRow
        label={t('ui.debug.jitter')}
        value={`${jitter.toFixed(1)}ms`}
        valueClass={jitter > 20 ? 'text-orange-400' : 'text-slate-300'}
      />
      {reconnects > 0 && (
        <DebugRow
          label={t('ui.debug.reconnects')}
          value={String(reconnects)}
          valueClass="text-yellow-400"
        />
      )}
    </DebugSection>
  );
};

const getFootprintValue = ({
  canPlaceFootprint,
}: {
  canPlaceFootprint: boolean | null;
}): string => {
  if (canPlaceFootprint === null) {
    return '-';
  }
  return yesNo({ value: canPlaceFootprint });
};

const getFootprintColor = ({
  canPlaceFootprint,
}: {
  canPlaceFootprint: boolean | null;
}): string => {
  if (canPlaceFootprint === null) {
    return 'text-slate-400';
  }
  return boolColor({ value: canPlaceFootprint });
};

const PlacementDebugSection = ({ info }: { info: PlacementDebugInfo }) => {
  const blockedObjectValue = info.blockedByObject
    ? `${t('ui.debug.yes')} (${info.blockingObjectType ?? '-'})`
    : t('ui.debug.no');

  return (
    <DebugSection title={t('ui.debug.placement')}>
      <DebugRow label={t('ui.debug.tile')} value={`${info.tileX}, ${info.tileY}`} />
      <DebugRow label={t('ui.debug.chunk')} value={`${info.chunkX}, ${info.chunkY}`} />
      <DebugRow
        label={t('ui.debug.chunkLoaded')}
        value={yesNo({ value: info.hasChunk })}
        valueClass={boolColor({ value: info.hasChunk })}
      />
      <DebugRow
        label={t('ui.debug.tileType')}
        value={info.tileType === null ? '-' : String(info.tileType)}
      />
      <DebugRow
        label={t('ui.debug.tileWalkable')}
        value={yesNo({ value: info.tileWalkable })}
        valueClass={boolColor({ value: info.tileWalkable })}
      />
      <DebugRow
        label={t('ui.debug.blockedObject')}
        value={blockedObjectValue}
        valueClass={boolColor({ value: info.blockedByObject, invert: true })}
      />
      <DebugRow
        label={t('ui.debug.blockedConstruction')}
        value={yesNo({ value: info.blockedByConstruction })}
        valueClass={boolColor({ value: info.blockedByConstruction, invert: true })}
      />
      <DebugRow
        label={t('ui.debug.canPlaceTile')}
        value={yesNo({ value: info.canPlaceAtTile })}
        valueClass={boolColor({ value: info.canPlaceAtTile })}
      />
      <DebugRow
        label={t('ui.debug.canPlaceFootprint')}
        value={getFootprintValue({ canPlaceFootprint: info.canPlaceFootprint })}
        valueClass={getFootprintColor({ canPlaceFootprint: info.canPlaceFootprint })}
      />
      <DebugRow
        label={t('ui.debug.reason')}
        value={info.footprintReason ?? '-'}
        valueClass={info.footprintReason ? 'text-red-400' : 'text-slate-400'}
      />
    </DebugSection>
  );
};

const TimingHeader = () => (
  <div class="flex justify-between gap-2 text-[10px] text-slate-600 border-b border-slate-700/30 pb-0.5 mb-0.5">
    <span class="w-20" />
    <span class="w-10 text-right">{t('ui.debug.last')}</span>
    <span class="w-10 text-right">{t('ui.debug.avg')}</span>
    <span class="w-10 text-right">{t('ui.debug.max')}</span>
  </div>
);

const TimingRow = ({ stats }: { stats: SectionStats }) => {
  const avgColor =
    stats.avg > 5 ? 'text-red-400' : stats.avg > 2 ? 'text-yellow-400' : 'text-slate-300';
  const maxColor =
    stats.max > 10 ? 'text-red-400' : stats.max > 5 ? 'text-yellow-400' : 'text-slate-300';

  return (
    <div class="flex justify-between gap-2">
      <span class="w-20 text-slate-500 truncate">{stats.name}</span>
      <span class="w-10 text-right text-slate-400">{stats.last.toFixed(1)}</span>
      <span class={`w-10 text-right ${avgColor}`}>{stats.avg.toFixed(1)}</span>
      <span class={`w-10 text-right ${maxColor}`}>{stats.max.toFixed(1)}</span>
    </div>
  );
};

const percentileColor = ({
  value,
  good,
  warn,
}: {
  value: number;
  good: number;
  warn: number;
}): string => {
  if (value > warn) {
    return 'text-red-400';
  }
  if (value > good) {
    return 'text-yellow-400';
  }
  return 'text-emerald-400';
};

const PercentilesSubsection = ({
  percentiles,
}: {
  percentiles: ProfileSnapshot['percentiles'];
}) => (
  <>
    <div class="text-[10px] text-slate-500 font-semibold mt-1.5">{t('ui.debug.percentiles')}</div>
    <DebugRow
      label="p50"
      value={`${percentiles.p50}ms`}
      valueClass={percentileColor({ value: percentiles.p50, good: 12, warn: 18 })}
    />
    <DebugRow
      label="p95"
      value={`${percentiles.p95}ms`}
      valueClass={percentileColor({ value: percentiles.p95, good: 20, warn: 33 })}
    />
    <DebugRow
      label="p99"
      value={`${percentiles.p99}ms`}
      valueClass={percentileColor({ value: percentiles.p99, good: 33, warn: 50 })}
    />
  </>
);

const StuttersSubsection = ({ stutters }: { stutters: ProfileSnapshot['stutters'] }) => (
  <>
    <div class="text-[10px] text-slate-500 font-semibold mt-1.5">{t('ui.debug.stutters')}</div>
    <DebugRow
      label={`${t('ui.debug.recent')} >20ms`}
      value={String(stutters.recent)}
      valueClass={stutters.recent > 0 ? 'text-yellow-400' : 'text-emerald-400'}
    />
    <DebugRow
      label={`${t('ui.debug.severe')} >50ms`}
      value={String(stutters.recentSevere)}
      valueClass={stutters.recentSevere > 0 ? 'text-red-400' : 'text-emerald-400'}
    />
    <DebugRow
      label={t('ui.debug.worst')}
      value={`${stutters.worst}ms`}
      valueClass={percentileColor({ value: stutters.worst, good: 20, warn: 50 })}
    />
    <DebugRow
      label={t('ui.debug.gcPauses')}
      value={String(stutters.estimatedGcPauses)}
      valueClass={stutters.estimatedGcPauses > 0 ? 'text-orange-400' : 'text-slate-300'}
    />
  </>
);

const FrameProfileDebugSection = ({ snapshot }: { snapshot: ProfileSnapshot }) => (
  <DebugSection title={t('ui.debug.frameProfile')}>
    {/* Game loop phases */}
    <div class="text-[10px] text-slate-500 font-semibold mt-1">{t('ui.debug.phases')}</div>
    <TimingHeader />
    {snapshot.phases.map((s) => (
      <TimingRow key={s.name} stats={s} />
    ))}

    {/* Render layers (sorted by avg, filtered > 0.05ms) */}
    {snapshot.layers.length > 0 && (
      <>
        <div class="text-[10px] text-slate-500 font-semibold mt-1.5">{t('ui.debug.layers')}</div>
        <TimingHeader />
        {snapshot.layers.map((s) => (
          <TimingRow key={s.name} stats={s} />
        ))}
      </>
    )}

    {/* Network processing time */}
    <div class="text-[10px] text-slate-500 font-semibold mt-1.5">{t('ui.debug.networkTime')}</div>
    <TimingHeader />
    <TimingRow stats={snapshot.network} />

    <PercentilesSubsection percentiles={snapshot.percentiles} />
    <StuttersSubsection stutters={snapshot.stutters} />
  </DebugSection>
);

const FrameProfileSection = () => {
  // Read signal to subscribe to updates (~2x/sec)
  const _version = frameProfileVersion.value;
  void _version;

  const snapshot = getProfileSnapshot();
  if (!snapshot) {
    return null;
  }
  return <FrameProfileDebugSection snapshot={snapshot} />;
};

export const DebugOverlay = () => {
  const isEnabled = debugEnabled.value;
  const flags = debugFlags.value;
  const placementInfo = placementDebug.value;

  if (!isEnabled) {
    return null;
  }

  const activeFlags = Object.entries(flags)
    .filter(([_, active]) => active)
    .map(([name]) => name.replace('show', ''));

  return (
    <div class="fixed top-4 left-4 z-50 pointer-events-none select-none">
      <div class="bg-slate-900/90 backdrop-blur-sm border border-slate-700/50 rounded-lg shadow-xl overflow-hidden min-w-64">
        {/* Header */}
        <div class="bg-slate-800/80 px-3 py-2 border-b border-slate-700/50 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span class="font-mono text-xs font-semibold text-slate-200">
              {t('ui.debug.debugMode')}
            </span>
          </div>
          <span class="text-[10px] text-slate-500 font-mono">{t('ui.debug.pressToClose')}</span>
        </div>

        {/* Content */}
        <div class="p-3 space-y-3 font-mono text-xs">
          {flags.showPerformance && <PerformanceDebugSection />}
          {flags.showNetworkInfo && <NetworkDebugSection />}

          <DebugSection title={t('ui.debug.activeFlags')}>
            {activeFlags.length > 0 ? (
              <div class="flex flex-wrap gap-1">
                {activeFlags.map((flag) => (
                  <span
                    key={flag}
                    class="px-1.5 py-0.5 bg-slate-700/50 rounded text-[10px] text-slate-300"
                  >
                    {flag}
                  </span>
                ))}
              </div>
            ) : (
              <span class="text-slate-500 text-[10px]">{t('ui.empty.noneActive')}</span>
            )}
          </DebugSection>

          {flags.showPlacement && placementInfo && <PlacementDebugSection info={placementInfo} />}

          {flags.showFrameProfile && <FrameProfileSection />}

          <DebugSection title={t('ui.debug.shortcuts')}>
            <div class="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px]">
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">1</kbd> {t('ui.debug.chunkBorders')}
              </span>
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">2</kbd> {t('ui.debug.origin')}
              </span>
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">3</kbd> {t('ui.debug.tileGrid')}
              </span>
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">4</kbd> {t('ui.debug.performance')}
              </span>
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">5</kbd> {t('ui.debug.entityIds')}
              </span>
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">6</kbd> {t('ui.debug.network')}
              </span>
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">7</kbd> {t('ui.debug.placement')}
              </span>
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">8</kbd> {t('ui.debug.frameProfile')}
              </span>
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">9</kbd> {t('ui.debug.enableAll')}
              </span>
              <span class="text-slate-500">
                <kbd class="px-1 bg-slate-700/50 rounded">0</kbd> {t('ui.debug.reset')}
              </span>
            </div>
          </DebugSection>
        </div>
      </div>
    </div>
  );
};
